<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <div 
    :class="[
      'bg-[var(--color-primary-100)] rounded-lg border border-[var(--color-primary-400)] flex shadow-sm transition-colors',
      compact ? 'p-2 gap-2.5 items-center' : 'p-3 gap-3 items-start',
      clickable ? 'cursor-pointer hover:bg-[var(--color-primary-200)]' : ''
    ]"
    @click="handleClick"
  >
    <div :class="[
      'rounded-md flex items-center justify-center shrink-0', 
      compact ? 'w-7 h-7' : 'w-8 h-8',
      activity.color
    ]">
      <component :is="activity.icon" :class="[compact ? 'w-3.5 h-3.5' : 'w-4 h-4', 'stroke-[2.5]']" />
    </div>
    <div class="flex-1 min-w-0">
      <h4 :class="[
        'font-semibold text-[var(--color-text)] leading-tight',
        compact ? 'text-xs truncate' : 'text-sm'
      ]">
        {{ activity.title }}
      </h4>
      <p :class="[
        'text-[var(--color-text)] opacity-60',
        compact ? 'text-[10px]' : 'text-xs mt-0.5'
      ]">
        {{ activity.time }}
      </p>
    </div>
    <ChevronRight v-if="clickable" :class="[
      'text-[var(--color-text)] opacity-40 shrink-0',
      compact ? 'w-3.5 h-3.5' : 'w-4 h-4 self-center'
    ]" />
  </div>
</template>

<script setup>
import { ChevronRight } from 'lucide-vue-next'

/**
 * Displays a single activity item (weight, health, adding goat....)
 * in a card format. Supports compact mode and click interactions.
 */
const props = defineProps({
  activity: { type: Object, required: true },
  clickable: { type: Boolean, default: true },
  compact: { type: Boolean, default: false }
})

const emit = defineEmits(['click'])

/**
 * Handles click events on the card.
 * Emits 'click' event with the activity object if clickable is true.
 */
function handleClick(event) {
  if (props.clickable) {
    emit('click', props.activity)
  }
}
</script>